import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { padNumber } from '@/lib/format'
import CoverPlate from '@/components/content/cover-plate'
import TechnologyList from '@/components/projects/technology-list'
import { SampleBadge } from '@/components/elements/sample-notice'

interface ProjectFeatureProps {
    project: ProjectSummary
    index: number
    layout?: 'wide' | 'stacked'
    headingLevel?: 'h2' | 'h3'
    priority?: boolean
    className?: string
}

/**
 * Editorial project feature. `wide` sets the plate and the text side by side
 * (the lead story); `stacked` puts the plate above the text.
 */
const ProjectFeature: FC<ProjectFeatureProps> = ({ project, index, layout = 'stacked', headingLevel: Heading = 'h3', priority = false, className }): ReactNode => {
    const number = padNumber(index)
    const href = `/projects/${project.slug}`
    const titleId = `project-${project.slug}-title`

    return (
        <article
            aria-labelledby={titleId}
            className={cn(
                'group relative',
                layout === 'wide' ? 'grid grid-cols-4 gap-x-4 gap-y-6 sm:gap-x-6 md:grid-cols-12 lg:gap-x-8' : 'flex flex-col gap-6',
                className
            )}
        >
            <CoverPlate
                id={`feature-${project.slug}`}
                title={project.title}
                schematic={project.schematic}
                cover={project.cover}
                coverAlt={project.coverAlt}
                number={number}
                label={project.categoryLabel}
                priority={priority}
                sizes={layout === 'wide' ? '(min-width: 768px) 58vw, 100vw' : '(min-width: 768px) 45vw, 100vw'}
                className={layout === 'wide' ? 'col-span-4 md:col-span-7' : undefined}
            />

            <div className={cn('flex flex-col gap-4', layout === 'wide' && 'col-span-4 md:col-span-5 md:justify-end md:pb-1')}>
                <div className='flex flex-wrap items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                    <span className='text-foreground'>{number}</span>
                    <span aria-hidden='true' className='h-px w-6 bg-border-strong' />
                    <span>{project.categoryLabel}</span>
                    <span aria-hidden='true' className='text-border-strong'>/</span>
                    <time dateTime={project.date}>{project.year}</time>
                    {project.sample && <SampleBadge />}
                </div>

                <Heading
                    id={titleId}
                    className={cn(
                        'font-heading tracking-[-0.02em] text-foreground',
                        layout === 'wide' ? 'text-title [font-weight:420]' : 'text-heading [font-weight:450]'
                    )}
                >
                    <Link href={href} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                        {project.title}
                    </Link>
                </Heading>

                <p className={cn('max-w-[46ch] text-muted-foreground', layout === 'wide' ? 'text-lede' : 'text-body')}>
                    {project.description}
                </p>

                <TechnologyList items={project.technologies} />

                <span className='mt-1 inline-flex items-center gap-2 font-mono text-label uppercase text-foreground'>
                    Read the case study
                    <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 ease-editorial group-hover:translate-x-1' />
                </span>
            </div>
        </article>
    )
}

export default ProjectFeature
